import React from 'react';
import { keyframes } from '@emotion/react';
import { useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

const STROKE_WIDTH = 6;
const DURATION = '1.5s';

/**
 * The highlight is built from segments that all share a leading edge, so they stack up into a
 * solid head and thin out behind it. Giving segment `i` an opacity of `1 / (i + 1)` makes the
 * composited alpha fall away linearly along the tail, and at this many segments the steps between
 * them are too small to see. A stroke can't carry a gradient along its own length, so stacking is
 * what stands in for one.
 */
const SEGMENT_COUNT = 16;
const TRAIL_SPAN = 0.55; // how much of the border the tail covers, as a fraction of its length
const TRAIL = [...Array(SEGMENT_COUNT).keys()].map(index => ({
    length: (TRAIL_SPAN * (index + 1)) / SEGMENT_COUNT,
    opacity: 1 / (index + 1)
}));

/**
 * One full lap of the border. The dash pattern spans the perimeter exactly once so the loop is
 * seamless, and each segment starts offset by its own length, which lines up every leading edge.
 */
const trace = (perimeter: number, dashLength: number) =>
    keyframes({
        from: { strokeDashoffset: dashLength },
        to: { strokeDashoffset: dashLength - perimeter }
    });

const Root = styled('svg')({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    overflow: 'visible', // the stroke straddles the edge, so half of it falls outside the viewport
    pointerEvents: 'none'
});

const Trail = styled('rect', {
    shouldForwardProp: prop => prop !== 'perimeter' && prop !== 'dashLength'
})<{ perimeter: number; dashLength: number }>(({ perimeter, dashLength }) => ({
    fill: 'none',
    strokeWidth: STROKE_WIDTH,
    strokeLinecap: 'round',
    animation: `${trace(perimeter, dashLength)} ${DURATION} linear infinite`
}));

interface LiveIndicatorBorderProps {
    liveIndicatorColour: string;
}

/**
 * A tinted highlight that continuously traces around the border, matching the native apps. It tells
 * the person scanning the code that they are looking at a live screen rather than a screenshot
 * someone has been given.
 */
export const LiveIndicatorBorder: React.FC<LiveIndicatorBorderProps> = ({ liveIndicatorColour }) => {
    const theme = useTheme();
    const ref = React.useRef<SVGSVGElement>(null);
    const [perimeter, setPerimeter] = React.useState(0);
    // The theme's radius can be a number or a CSS length, but the perimeter maths needs a number.
    const radius = Number.parseFloat(`${theme.shape.borderRadius}`);

    React.useEffect(() => {
        const element = ref.current;
        if (!element) {
            return undefined;
        }
        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            const cornerRadius = Math.min(radius, width / 2, height / 2);
            setPerimeter(
                2 * (width - 2 * cornerRadius) + 2 * (height - 2 * cornerRadius) + 2 * Math.PI * cornerRadius
            );
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [radius]);

    return (
        <Root ref={ref} aria-hidden focusable="false">
            {perimeter > 0 &&
                TRAIL.map(segment => {
                    const dashLength = segment.length * perimeter;
                    return (
                        <Trail
                            key={segment.length}
                            perimeter={perimeter}
                            dashLength={dashLength}
                            x={0}
                            y={0}
                            width="100%"
                            height="100%"
                            rx={radius}
                            stroke={liveIndicatorColour}
                            opacity={segment.opacity}
                            strokeDasharray={`${dashLength} ${perimeter - dashLength}`}
                        />
                    );
                })}
        </Root>
    );
};
